import { Component } from '@theme/component';
import { debounce, onAnimationEnd, onDocumentLoaded } from '@theme/utilities';
import { getSearchSuggestions, getSearchProducts } from '@theme/searchspring-client';
import { getSearchContent } from '@theme/shopify-search-client';
import { renderSearchResults } from '@theme/search-results-renderer';
import { createAbortControllerManager } from '@theme/abort-controllers';
import { createKeyboardNavigator } from '@theme/keyboard-navigator';
import { buildColorLabelToCodeMap } from '@theme/searchspring-core';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} searchContent - The main search results container.
 * @property {HTMLElement} searchSuggestions - The search suggestions container.
 * @property {HTMLElement} searchProducts - The search products container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} resultsContainer - The results container element.
 * @property {HTMLTemplateElement} [moneyFormat] - The money format template.
 * @extends {Component<Refs>}
 */
class MainSearchComponent extends Component {
  requiredRefs = ['searchInput', 'resultsContainer'];

  #controller = new AbortController();

  /**
   * @type {ReturnType<typeof createAbortControllerManager>}
   */
  #abortManager = createAbortControllerManager();

  /**
   * @type {ReturnType<typeof createKeyboardNavigator> | null}
   */
  #keyboardNavigator = null;

  /** @type {Map<string, string>} */
  #colorLabelToCodeMap = new Map();

  /** @type {Record<string, string[]>} */
  #colorGroupsMap = {};

  /** @type {boolean} */
  #isB2B = false;

  connectedCallback() {
    const { signal } = this.#controller;

    super.connectedCallback();

    const colorLabelsAttr = this.getAttribute('data-color-labels');
    if (colorLabelsAttr) {
      try {
        this.#colorLabelToCodeMap = buildColorLabelToCodeMap(JSON.parse(colorLabelsAttr));
      } catch (e) { /* ignore */ }
    }

    const colorGroupsAttr = this.getAttribute('data-color-groups');
    if (colorGroupsAttr) {
      try {
        const raw = JSON.parse(colorGroupsAttr);
        for (const [key, codes] of Object.entries(raw)) {
          this.#colorGroupsMap[key.toLowerCase()] = /** @type {string[]} */ (codes);
        }
      } catch (e) { /* ignore */ }
    }

    this.#isB2B = this.getAttribute('data-is-b2b') === 'true';

    if (this.refs.searchInput.value.length) {
      this.#showResetButton();
    }

    // Initialize keyboard navigator
    this.#keyboardNavigator = createKeyboardNavigator(this, {
      getAllResultsItems: () => this.#allResultsItems,
      onEscape: (/** @type {KeyboardEvent} */ event) => this.closeSearchModal(event),
    });

    document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#abortManager.abortAll();
  }

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   * @param {InputEvent} event - The input event from the search input field
   */
  search = debounce(async (event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#keyboardNavigator?.reset();

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    const siteId = this.dataset.siteId || '';
    const suggestionsQty = this.dataset.suggestionsQty || '4';
    const productsQty = this.dataset.productsQty || '10';
    const contentQty = this.dataset.contentQty || '10';
    const sectionId = this.dataset.sectionId;

    // Create abort controllers for parallel requests
    const suggestionsAbortController = this.#abortManager.createController('suggestions');
    const contentAbortController = this.#abortManager.createController('content');

    // Fire Shopify content and SearchSpring suggestions in parallel
    const contentReq = sectionId
      ? getSearchContent(sectionId, searchTerm, {
          contentQty,
          signal: contentAbortController.signal,
        })
      : Promise.resolve(null);

    const suggestionsReq = getSearchSuggestions(siteId, searchTerm, {
      suggestionsQty,
      productsQty,
      signal: suggestionsAbortController.signal,
    });

    // Wait for suggestions (we need them to determine product request)
    const suggestions = await suggestionsReq.catch(() => []);
    const suggestionToUse = suggestions.length > 0 && suggestions[0] ? suggestions[0] : searchTerm;

    // Fire dependent product request
    const productsAbortController = this.#abortManager.createController('products');
    const productsReq = getSearchProducts(siteId, suggestionToUse, {
      productsQty,
      signal: productsAbortController.signal,
    });

    // Wait for everything
    const [contentRes, productsRes] = await Promise.allSettled([contentReq, productsReq]);

    const contentHTML = contentRes.status === 'fulfilled' && contentRes.value ? contentRes.value : null;
    const productsData = productsRes.status === 'fulfilled' ? productsRes.value : { results: [], didYouMean: null };
    const { results: products, didYouMean } = productsData;

    if (this.refs.searchInput.value.trim() === searchTerm) {
      const moneyFormatTemplate = this.refs.moneyFormat?.content?.textContent || undefined;
      renderSearchResults({
        searchContent: this.refs.searchContent,
        searchSuggestions: this.refs.searchSuggestions,
        searchProducts: this.refs.searchProducts,
        resultsContainer: this.refs.resultsContainer,
        contentHTML,
        suggestions,
        products,
        didYouMean,
        searchTerm,
        moneyFormatTemplate,
        colorLabelToCodeMap: this.#colorLabelToCodeMap,
        colorGroupsMap: this.#colorGroupsMap,
        isB2B: this.#isB2B,
      });
      this.#resetScrollPositions();
    }
  }, 300);

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (this.#keyboardNavigator) {
      this.#keyboardNavigator.handleKeyDown(event);
    }
  };

  /**
   * Get all search result items from both product slides and dropdown lists.
   * Used for keyboard navigation through search results.
   * @returns {HTMLElement[]} Array of all search result items
   */
  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll('.search-products__slides, .search-dropdown__list')
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('search-products__slides')) {
          return Array.from(container.querySelectorAll('slideshow-slide'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"]'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.toggleSearchModal(event);
    }
  };

  /**
   * Get the search modal element (cached).
   * @returns {Element | null} The modal element
   */
  #getModal() {
    if (!this._cachedModal) {
      this._cachedModal = this.closest('.search-modal');
    }
    return this._cachedModal;
  }

  /**
   * Get all search button elements (cached).
   * @returns {NodeListOf<Element>} The search button elements
   */
  #getSearchButtons() {
    if (!this._cachedSearchButtons) {
      this._cachedSearchButtons = document.querySelectorAll('search-button');
    }
    return this._cachedSearchButtons;
  }

  /**
   * Set the modal state (open or closed).
   * @param {boolean} isOpen - Whether to open the modal
   * @param {Event} [event] - The event that triggered the state change
   */
  #setModalState(isOpen, event) {
    const modal = this.#getModal();
    const searchButtons = this.#getSearchButtons();

    event?.preventDefault();

    if (isOpen) {
      modal?.classList.add('active');
      this.refs.searchInput.focus();
      searchButtons.forEach((btn) => {
        btn.classList.add('active');
      });
    } else {
      modal?.classList.remove('active');
      this.refs.searchInput.blur();
      searchButtons.forEach((btn) => {
        btn.classList.remove('active');
      });
      this.#resetSearch();
    }
  }

  /**
   * Toggles the search modal state (open/closed).
   * @param {Event} event - The event that triggered the toggle
   */
  toggleSearchModal(event) {
    const modal = this.#getModal();
    const isOpen = modal?.classList.contains('active');
    this.#setModalState(!isOpen, event);
  }

  /**
   * Opens the search modal and focuses the search input.
   * @param {Event} event - The event that triggered the open action
   */
  openSearchModal(event) {
    this.#setModalState(true, event);
  }

  /**
   * Closes the search modal and resets the search state.
   * @param {Event} event - The event that triggered the close action
   */
  closeSearchModal(event) {
    this.#setModalState(false, event);
  }

  /**
   * Hides the reset button by removing the 'active' class.
   */
  #hideResetButton() {
    const { resetButton } = this.refs;
    resetButton?.classList.remove('active');
  }

  /**
   * Shows the reset button by adding the 'active' class.
   */
  #showResetButton() {
    const { resetButton } = this.refs;
    resetButton?.classList.add('active');
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Internal method that resets the search state.
   * Hides results container, clears input, resets keyboard navigation, and aborts all pending requests.
   */
  #resetSearch = async () => {
    const { searchInput, resultsContainer } = this.refs;

    if (resultsContainer instanceof HTMLElement) {
      resultsContainer.classList.remove('active');
    }

    this.#keyboardNavigator?.reset();
    searchInput.value = '';
    this.#hideResetButton();

    this.#abortManager.abortAll();

    this.#resetScrollPositions();
  };

  /**
   * Resets scroll positions for search results containers.
   * Waits for animations to complete before resetting scroll to top.
   */
  #resetScrollPositions() {
    const { resultsContainer } = this.refs;

    if (resultsContainer instanceof HTMLElement) {
      onAnimationEnd(resultsContainer, () => {
        resultsContainer.scrollTop = 0;
      });
    }
  }
}

if (!customElements.get('main-search-component')) {
  customElements.define('main-search-component', MainSearchComponent);
}
